/**
 * Shared grouping for Thermoduct floor-heating product categories.
 *
 * Every product sits in a `product.category` named "Vloer- en wandverwarming <X>"
 * (Collectoren, Leidingen, Accessoires …). We surface the category id + (prefix-
 * stripped) name on each line and group lines by it, in a fixed build-logical order
 * with a brand accent colour per group. Used by the voorraad LeveringDetailModal and
 * the warehouse WerfMateriaalTab so both read identically.
 */

/** Any line carrying a resolved product category. */
export interface CategLine {
  categId: number | null;
  categName: string | null;
}

// Fixed display order — lower = shown first. Uncategorised / non-floor-heating
// products fall to "Overige" last.
const CATEG_ORDER: Record<number, number> = { 21: 0, 22: 1, 20: 2, 23: 3, 24: 4 };
const OVERIGE_ORDER = 99;

// Brand accent colour per category (Tailwind bg-* classes from index.css @theme).
const CATEG_COLOR: Record<number, string> = {
  21: 'bg-td-blue', // Collectoren
  22: 'bg-td-blue-dark', // Accessoires collectoren
  20: 'bg-td-green', // Leidingen
  23: 'bg-td-purple', // Accessoires systemen
  24: 'bg-td-orange', // Isolatie, onder- en bovenvloer
};

// Display-label overrides — show a friendlier term than the raw Odoo category name.
const CATEG_LABEL_OVERRIDE: Record<number, string> = {
  20: 'Buis', // Odoo "Leidingen" → Buis (the warehouse term for the pipe)
};

export function categLabel(categId: number | null, categName: string | null): string {
  return (categId !== null && CATEG_LABEL_OVERRIDE[categId]) || categName || 'Overige';
}

export function categOrder(categId: number | null): number {
  return categId !== null ? (CATEG_ORDER[categId] ?? OVERIGE_ORDER) : OVERIGE_ORDER;
}

export function categColor(categId: number | null): string {
  return (categId !== null && CATEG_COLOR[categId]) || 'bg-slate-300';
}

export interface CategGroup<T> {
  categId: number | null;
  label: string;
  color: string;
  count: number;
  items: T[];
}

/**
 * Group lines by product category, ordered by the fixed priority (Overige last,
 * then alphabetically within a tie). Preserves source order of items within a group.
 * Accepts lines whose category fields may be absent (treated as uncategorised).
 */
export function groupByCateg<T extends Partial<CategLine>>(items: T[]): CategGroup<T>[] {
  const byCateg = new Map<number | null, CategGroup<T>>();
  for (const it of items) {
    const key = it.categId ?? null;
    let g = byCateg.get(key);
    if (!g) {
      g = { categId: key, label: categLabel(key, it.categName ?? null), color: categColor(key), count: 0, items: [] };
      byCateg.set(key, g);
    }
    g.count += 1;
    g.items.push(it);
  }
  return [...byCateg.values()].sort((a, b) => {
    const d = categOrder(a.categId) - categOrder(b.categId);
    return d !== 0 ? d : a.label.localeCompare(b.label);
  });
}
